import type { OrderComplete } from '@/features/orders/domain/model/order';
import { calculateOrderFinancials } from '@/features/orders/domain/service/order-financials';

type PrintableItem = OrderComplete['order_items'][number];

/**
 * Deja el payload de impresión con UNA sola fuente de descuento.
 *
 * 1. Total: el driver imprime `order.total` tal cual, así que una orden grabada con el bruto
 *    histórico (p. ej. D63: $11.000 con 40% OFF) imprimiría el total sin descuento. Se manda el
 *    neto de `calculateOrderFinancials` (respeta el neto persistido y recalcula el histórico).
 * 2. Descuento: el driver imprime la promo embebida en cada ítem (`order_product_promo`) **y**
 *    `order_discounts`. Con ambas fuentes el renglón "Descuentos" salía duplicado (10% sobre
 *    $11.000 → $2.200 en vez de $1.100). Se quitan las promos de los ítems y se adjunta un único
 *    renglón sintético con el descuento efectivo (promo + cupón) para que el ticket cuadre:
 *    Subtotal (ítems a precio de lista) − Descuento = Total. No se persiste nada.
 */
export function normalizeOrderForPrint(order: OrderComplete): OrderComplete {
  const financials = calculateOrderFinancials(order.order_items ?? [], order.order_discounts ?? undefined, order.total);

  // Sin ítems no se puede reconstruir el bruto: se manda el total persistido sin tocarlo.
  if (financials.subtotalBruto <= 0) return order;

  return {
    ...order,
    total: financials.totalNeto,
    order_items: order.order_items.map(stripItemPromo),
    order_discounts:
      financials.totalDiscountAmount > 0
        ? {
            discount_code: financials.discountCode || 'Promo',
            discount: financials.totalDiscountAmount,
            discount_type: 'fixed',
          }
        : order.order_discounts,
  };
}

/**
 * El driver imprime la promo del ítem además de `order_discounts`: se elimina la clave para que
 * el descuento se imprima una sola vez (y no aparezca en el JSON aunque el ítem la traiga).
 */
function stripItemPromo(item: PrintableItem): PrintableItem {
  const printable = { ...item } as Record<string, unknown>;
  delete printable.order_product_promo;
  return printable as unknown as PrintableItem;
}
